import type React from 'react';
import { Link } from 'react-router-dom';
import type { MeasuringPointWithProject, ProjectOverviewRow } from '../types';
import type { UseOrderAndPaginationControlReturn } from '../../../table/hooks';
import { SortButtons } from '../../../table/Table';
import { ProjectMeasuringPointRow } from './ProjectMeasuringPointRow/ProjectMeasuringPointRow';
import {
    getMeasuringPointRowKey,
    getNewMeasuringPointUrl,
    uniqueMeasuringPoints,
} from '../utils/measuringPoints';
import classNames from '../../../../utils/classNames';
import { STANDALONE_COLUMN_WIDTHS_REM } from '../table/overviewGrid';

export const MEASURING_POINT_HEADER_ROW_CLASSES = 'bg-gray-100 text-xs tracking-wide text-gray-500';

const MEASURING_POINTS_MESSAGE_CLASSES =
    'rounded border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-xs text-gray-500';

interface ProjectMeasuringPointColumnHeader {
    key: keyof typeof STANDALONE_COLUMN_WIDTHS_REM | 'settings';
    label: string;
    align: string;
}

function getStandaloneColumnWidthRem({ key }: ProjectMeasuringPointColumnHeader): number {
    if (key === 'settings') {
        throw new Error('The Standalone table has no configuration column.');
    }
    return STANDALONE_COLUMN_WIDTHS_REM[key];
}

export function getMeasuringPointColumnHeaders(
    showSettingsSourceColumn: boolean
): ProjectMeasuringPointColumnHeader[] {
    return [
        {
            key: 'name',
            label: gettext('NAME'),
            align: 'text-left',
        },
        {
            key: 'city',
            label: gettext('CITY'),
            align: 'text-left',
        },
        {
            key: 'status',
            label: gettext('STATUS'),
            align: 'text-left',
        },
        {
            key: 'device',
            label: gettext('DEVICE'),
            align: 'text-left',
        },
        {
            key: 'lastContact',
            label: gettext('LAST_CONTACT'),
            align: 'text-left',
        },
        ...(showSettingsSourceColumn
            ? [
                  {
                      key: 'settings',
                      label: gettext('CONFIGURATION'),
                      align: 'text-left',
                  } satisfies ProjectMeasuringPointColumnHeader,
              ]
            : []),
        {
            key: 'options',
            label: gettext('OPTIONS'),
            align: 'text-right',
        },
    ];
}

export function MeasuringPointHeaderCell({
    header: { key, label, align },
    orderAndPaginationControl,
}: {
    header: ProjectMeasuringPointColumnHeader;
    orderAndPaginationControl?: UseOrderAndPaginationControlReturn;
}): React.ReactElement {
    return (
        <th className={classNames('px-4 py-2', align)} scope="col">
            {label}
            {orderAndPaginationControl?.sortableFields.includes(key) && (
                <SortButtons
                    columnKey={key}
                    columnTitle={label}
                    orderBy={orderAndPaginationControl.orderBy}
                    setOrderBy={orderAndPaginationControl.setOrderBy}
                />
            )}
        </th>
    );
}

export function MeasuringPointsEmptyMessage({
    project,
}: {
    project?: ProjectOverviewRow;
}): React.ReactElement {
    const createMeasuringPointUrl = project ? getNewMeasuringPointUrl(project) : null;

    return (
        <div className={MEASURING_POINTS_MESSAGE_CLASSES}>
            {gettext('PROJECT_NO_MEASURING_POINTS')}
            {createMeasuringPointUrl && (
                <Link
                    to={createMeasuringPointUrl}
                    className="ml-1 font-medium underline"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {gettext('CREATE_MEASURING_POINT')}
                </Link>
            )}
        </div>
    );
}

export function MeasuringPointsLoadFailedMessage(): React.ReactElement {
    return (
        <div role="alert" className={MEASURING_POINTS_MESSAGE_CLASSES}>
            {gettext('GENERIC_SERVER_ERROR')}
        </div>
    );
}

interface Props {
    measuringPoints: MeasuringPointWithProject[];
    orderAndPaginationControl?: UseOrderAndPaginationControlReturn;
}

/** Measuring points without a project, rendered as their own table. */
export function ProjectMeasuringPointList({
    measuringPoints,
    orderAndPaginationControl,
}: Props): React.ReactElement {
    const rows = uniqueMeasuringPoints(measuringPoints);

    if (!rows.length) {
        return (
            <div className="p-4">
                <MeasuringPointsEmptyMessage />
            </div>
        );
    }

    const headers = getMeasuringPointColumnHeaders(false);
    const tableMinWidth = `${headers.reduce(
        (total, header) => total + getStandaloneColumnWidthRem(header),
        0
    )}rem`;

    return (
        <table className="w-full table-fixed text-xs" style={{ minWidth: tableMinWidth }}>
            <colgroup>
                {headers.map((header) => (
                    <col
                        key={header.key}
                        style={{ width: `${getStandaloneColumnWidthRem(header)}rem` }}
                    />
                ))}
            </colgroup>
            <caption className="sr-only">{gettext('PROJECT_MEASURING_POINTS_LIST')}</caption>
            <thead>
                <tr className={MEASURING_POINT_HEADER_ROW_CLASSES}>
                    {headers.map((header) => (
                        <MeasuringPointHeaderCell
                            key={header.key}
                            header={header}
                            orderAndPaginationControl={orderAndPaginationControl}
                        />
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((measuringPoint) => (
                    <ProjectMeasuringPointRow
                        key={getMeasuringPointRowKey(measuringPoint)}
                        measuringPoint={measuringPoint}
                        layout="standalone"
                    />
                ))}
            </tbody>
        </table>
    );
}
